import type { JSX } from "react";
import { useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api.js";
import { PageHeader } from "../components/PageHeader.js";
import { Button } from "../components/ui/button.js";
import { Badge } from "../components/ui/badge.js";
import { Card } from "../components/ui/card.js";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table.js";

interface Doc {
  id: string;
  original_filename: string;
  mime_type: string;
  size_bytes: string;
  status: "pending" | "processing" | "processed" | "failed";
  classified_type: string | null;
  created_at: string;
}

export function Documents(): JSX.Element {
  const fileRef = useRef<HTMLInputElement>(null);
  const qc = useQueryClient();
  const docs = useQuery({ queryKey: ["docs"], queryFn: () => api.list<Doc>("/documents") });
  const upload = useMutation({
    mutationFn: (f: File) => api.uploadFile(f),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["docs"] }),
  });

  return (
    <>
      <PageHeader title="Documents">
        <input
          ref={fileRef}
          type="file"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) upload.mutate(f);
          }}
        />
        <Button onClick={() => fileRef.current?.click()} disabled={upload.isPending}>
          {upload.isPending ? "Uploading…" : "Upload"}
        </Button>
      </PageHeader>

      <Card className="overflow-hidden p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Filename</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Size</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Uploaded</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {docs.data?.items.map((d) => (
              <TableRow key={d.id}>
                <TableCell className="font-medium">{d.original_filename}</TableCell>
                <TableCell>{d.classified_type ?? d.mime_type}</TableCell>
                <TableCell>{(Number(d.size_bytes) / 1024).toFixed(1)} KB</TableCell>
                <TableCell>
                  <Badge
                    variant={
                      d.status === "processed"
                        ? "good"
                        : d.status === "failed"
                          ? "destructive"
                          : "warn"
                    }
                  >
                    {d.status}
                  </Badge>
                </TableCell>
                <TableCell>{new Date(d.created_at).toLocaleString()}</TableCell>
              </TableRow>
            ))}
            {docs.data?.items.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-muted-foreground py-8 text-center">
                  No documents yet.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
    </>
  );
}
